import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ExternalLink, Inbox } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Button from '@/shared/components/elements/Button.jsx';
import useSelectedTenant from '@/shared/hooks/useSelectedTenant.js';
import { QUERY_KEYS } from '@/shared/constant/queryKeys.js';
import { getDashboardsByDevice } from '@/services/dashboardService.js';

export default function DeviceDashboardsModal({
  isOpen,
  onClose,
  onOpenDashboard,
  device,
}) {
  const { tenantId } = useSelectedTenant();
  const deviceEui = device?.eui;
  const {
    data: dashboards = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.DASHBOARD_BY_DEVICE(tenantId, deviceEui),
    queryFn: () => getDashboardsByDevice(deviceEui),
    select: (response) => response.data?.dashboards || [],
    enabled: Boolean(isOpen && tenantId && deviceEui),
  });

  const openDashboard = (dashboard) => {
    onClose();
    onOpenDashboard?.(dashboard);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`داشبوردهای ${device?.name || 'دستگاه'}`}
      icon={<BarChart3 size={20} />}
      size="lg"
      contentClassName="p-4 sm:p-5"
    >
      {isLoading ? (
        <div className="space-y-3" aria-label="در حال دریافت داشبوردها">
          {[1, 2].map((item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50"
            />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          دریافت داشبوردهای این دستگاه ناموفق بود. کمی بعد دوباره تلاش کنید.
        </div>
      ) : dashboards.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Inbox size={42} className="text-gray-400" />
          <div>
            <p className="font-bold text-gray-800 dark:text-gray-100">
              داشبوردی برای این دستگاه وجود ندارد
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              پس از تخصیص قالب سازگار، داشبورد دستگاه اینجا نمایش داده می‌شود.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {dashboards.map((dashboard) => (
            <div
              key={dashboard.id}
              className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700"
            >
              <div className="min-w-0">
                <p className="truncate font-bold text-gray-900 dark:text-gray-100">
                  {dashboard.name}
                </p>
                {dashboard.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                    {dashboard.description}
                  </p>
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                icon={<ExternalLink size={16} />}
                onClick={() => openDashboard(dashboard)}
                className="shrink-0"
              >
                مشاهده داشبورد
              </Button>
            </div>
          ))}
        </div>
      )}
    </BaseModal>
  );
}

DeviceDashboardsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenDashboard: PropTypes.func,
  device: PropTypes.shape({
    eui: PropTypes.string,
    name: PropTypes.string,
  }),
};
